import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { ApiError } from "./api";
import { ConnectExtension } from "./pages/ConnectExtension";
import { Login } from "./pages/Login";
import { NotesPage } from "./pages/Notes";
import { Shared } from "./pages/Shared";
import { Files } from "./pages/Files";
import { BillingReturn } from "./pages/BillingReturn";
import { Settings } from "./pages/Settings";
import { XNotes } from "./pages/XNotes";
import { Signup } from "./pages/Signup";
import { useSession } from "./queries";
import { navigate, useLocation } from "./router";
import "./styles.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      refetchOnWindowFocus: true,
    },
  },
});

const PUBLIC_PATHS = new Set(["/login", "/signup"]);

function App() {
  const { path } = useLocation();
  const session = useSession();
  const user = session.data;

  useEffect(() => {
    if (session.isPending) return;
    if (!user && !PUBLIC_PATHS.has(path)) navigate(`/login?next=${encodeURIComponent(path + window.location.search)}`, { replace: true });
    if (user && PUBLIC_PATHS.has(path)) navigate("/", { replace: true });
  }, [session.isPending, user, path]);

  if (session.isPending) return <div className="splash" aria-busy="true" />;
  if (session.isError) {
    return (
      <div className="splash">
        <p>Can't reach ccnote right now.</p>
        <button className="btn" onClick={() => session.refetch()}>Try again</button>
      </div>
    );
  }

  if (path === "/login") return <Login />;
  if (path === "/signup") return <Signup />;
  if (!user) return null;
  if (path === "/settings") return <Settings user={user} />;
  if (path === "/files") return <Files />;
  if (path === "/x-notes") return <XNotes />;
  if (path === "/billing/return") return <BillingReturn />;
  if (path === "/connect/extension") return <ConnectExtension user={user} />;
  return <NotesPage user={user} />;
}

/** Shared-note links (/s/<token>) are public and never touch the sign-in flow. */
function Root() {
  const { path } = useLocation();
  const shared = path.match(/^\/s\/([^/]+)$/);
  return shared ? <Shared token={shared[1]!} /> : <App />;
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <Root />
    </QueryClientProvider>
  </StrictMode>,
);
